import { useMemo } from 'react';
import type { ICellRendererParams, RowClickedEvent } from 'ag-grid-community';
import FalconCardHeader from '@/components/common/FalconCardHeader';
import SubtleBadge from '@/components/common/SubtleBadge';
import { IndexerIcon } from './IndexerIcon';
import { Button, Card, OverlayTrigger, Spinner, Tooltip } from 'react-bootstrap';
import type { ProjectJob, ProjectJobTrigger } from '../api/types';
import { canRetryFailedJob } from '../lib/job-retry';
import { formatRelativeFa, formatTime, toUtcIso } from '../lib/datetime';
import {
  canStopJob,
  jobHistoryTypeLabel,
  jobSearchEngineLabel,
  jobStateLabel,
  jobStateTone,
} from '../lib/job-labels';
import { HISTORY_WINDOW_LABEL } from '../lib/job-range';
import { formatFaNumber } from '../lib/utils';
import { guideTooltipHtml } from './CoverageHelpTip';
import {
  InspectJobBulkMenu,
  type BulkNotify,
} from './InspectBulkMenu';
import { IndexJobBulkMenu, isIndexJobKind } from './IndexBulkMenu';
import { IndexerAgGrid } from './IndexerAgGrid';
import { IndexerTablePreload } from './preloads/IndexerPreloads';

function stateSubtleTone(
  state: ProjectJob['state'],
): 'success' | 'danger' | 'warning' | 'primary' | 'secondary' {
  return jobStateTone(state);
}

function canRetryJob(job: ProjectJob) {
  return canRetryFailedJob(job);
}

type ColumnGuide = {
  title: string;
  paragraphs: string[];
};

const HISTORY_COLUMN_GUIDES = {
  indexStatus: {
    title: 'وضعیت ایندکس',
    paragraphs: [
      'این ستون می‌گوید از نظر گوگل، چند آدرس ایندکس‌شده و چند آدرس ایندکس‌نیست هستند.',
      'یعنی نتیجهٔ واقعی پوشش ایندکس (مثلاً Submitted and indexed در مقابل URL is unknown یا Excluded).',
      'با ستون «نتیجه» فرق دارد: اینجا دربارهٔ ایندکس بودن صفحه حرف می‌زنیم، نه دربارهٔ موفقیت/شکست خودِ درخواست API.',
    ],
  },
  outcome: {
    title: 'نتیجه',
    paragraphs: [
      'موفق / ناموفق یعنی خودِ درخواست گرفتن وضعیت از گوگل (یا ارسال ایندکس) برای هر آدرس چطور تمام شده است — نه اینکه صفحه ایندکس شده باشد.',
      'مثال: درخواست بازرسی با موفقیت جواب گرفته (موفق) ولی جواب گوگل می‌گوید صفحه ایندکس‌نیست؛ آن وقت در ستون نتیجه «موفق» و در وضعیت ایندکس «ایندکس‌نیست» می‌بینید.',
      'اگر سهمیه، کلید، دسترسی Search Console یا شبکه مشکل داشته باشد، آن آدرس «ناموفق» می‌شود.',
      'وضعیت جاب (تمام‌شده، ناقص، در حال اجرا و …) و نوار پیشرفت هم مربوط به پیشرفت همین درخواست است.',
    ],
  },
  requestedAt: {
    title: 'زمان درخواست',
    paragraphs: [
      'زمان ثبت همین درخواست در سیستم را نشان می‌دهد (نسبی و مطلق، به وقت تهران).',
      'زمان آخرین خزش گوگل نیست؛ آن را داخل جزئیات هر آدرس می‌بینید.',
    ],
  },
  details: {
    title: 'جزئیات',
    paragraphs: [
      'با این دکمه نتایج همین درخواست باز می‌شود: لیست آدرس‌ها، پوشش ایندکس، و تاریخچهٔ هر URL.',
      'روی خود ردیف هم می‌توانید کلیک کنید؛ همان بخش «نتایج این درخواست» پایین صفحه باز می‌شود.',
    ],
  },
  schedule: {
    title: 'زمان‌بندی',
    paragraphs: [
      'نام زمان‌بندی‌ای که این اجرای خودکار از آن آمده است.',
    ],
  },
  runType: {
    title: 'نوع اجرا',
    paragraphs: [
      'اگر درخواست را خودتان فرستاده باشید «دستی» است؛ اگر از زمان‌بندی آمده باشد نام همان زمان‌بندی نمایش داده می‌شود.',
    ],
  },
  type: {
    title: 'نوع',
    paragraphs: [
      'نوع درخواست (مثلاً درخواست ایندکس یا حذف از نتایج).',
    ],
  },
  engine: {
    title: 'موتور جستجو',
    paragraphs: [
      'موتوری که این درخواست به آن ارسال شده است: گوگل یا Bing / Yandex (IndexNow).',
    ],
  },
} as const satisfies Record<string, ColumnGuide>;

function OutcomeCell({
  job,
  showRetry,
  retryBusy,
  retryingThis,
  onRetry,
  showStop,
  stopBusy,
  stoppingThis,
  onStop,
}: {
  job: ProjectJob;
  showRetry: boolean;
  retryBusy: boolean;
  retryingThis: boolean;
  onRetry?: (job: ProjectJob) => void;
  showStop: boolean;
  stopBusy: boolean;
  stoppingThis: boolean;
  onStop?: (job: ProjectJob) => void;
}) {
  const ok = job.successCount;
  const fail = job.failedCount;
  const processed = ok + fail;
  const total = Math.max(job.total, processed);
  const pending = Math.max(0, total - processed);
  const error = jobMetaError(job);
  const stateLabel = jobStateLabel(job.state);
  const stateBadgeLabel =
    error?.httpStatus != null
      ? `${stateLabel} (${error.httpStatus})`
      : stateLabel;

  const stateBadge = (
    <SubtleBadge
      bg={stateSubtleTone(job.state)}
      pill
      className="fs--2"
      style={{ lineHeight: 1.35 }}
    >
      {stateBadgeLabel}
    </SubtleBadge>
  );

  return (
    <div className="history-outcome py-1">
      <div className="history-outcome-top">
        <span className="text-success fs--2">{formatFaNumber(ok)} موفق</span>
        <span className="text-danger fs--2">{formatFaNumber(fail)} ناموفق</span>
        <span className="history-outcome-pipe" aria-hidden>
          |
        </span>
        {error?.message ? (
          <span
            className="d-inline-flex"
            onClick={(event) => event.stopPropagation()}
            onMouseDown={(event) => event.stopPropagation()}
          >
            <OverlayTrigger
              placement="top"
              overlay={
                <Tooltip id={`job-error-${job.id}`}>
                  <span
                    className="d-inline-block text-start"
                    dir="auto"
                    style={{ maxWidth: 320, whiteSpace: 'normal' }}
                  >
                    {error.message}
                  </span>
                </Tooltip>
              }
            >
              <span className="d-inline-flex">{stateBadge}</span>
            </OverlayTrigger>
          </span>
        ) : (
          stateBadge
        )}
        {showStop && canStopJob(job) && (
          <Button
            size="sm"
            variant="outline-warning"
            className="fs--2 py-0 px-2 text-nowrap"
            disabled={stopBusy}
            onClick={(event) => {
              event.stopPropagation();
              onStop?.(job);
            }}
          >
            {stoppingThis && (
              <Spinner
                size="sm"
                animation="border"
                className="me-1"
                style={{ width: 12, height: 12 }}
              />
            )}
            توقف
          </Button>
        )}
        {showRetry && canRetryJob(job) && (
          <Button
            size="sm"
            variant="outline-danger"
            className="fs--2 py-0 px-2 text-nowrap"
            disabled={retryBusy}
            onClick={(event) => {
              event.stopPropagation();
              onRetry?.(job);
            }}
          >
            {retryingThis && (
              <Spinner
                size="sm"
                animation="border"
                className="me-1"
                style={{ width: 12, height: 12 }}
              />
            )}
            تلاش مجدد
          </Button>
        )}
      </div>
      {total > 0 && (
        <div className="history-outcome-bar" aria-hidden>
          {ok > 0 && (
            <span
              className="history-outcome-ok"
              style={{ flexGrow: ok, flexBasis: 0 }}
            />
          )}
          {fail > 0 && (
            <span
              className="history-outcome-fail"
              style={{ flexGrow: fail, flexBasis: 0 }}
            />
          )}
          {pending > 0 && (
            <span
              className="history-outcome-pending"
              style={{ flexGrow: pending, flexBasis: 0 }}
            />
          )}
        </div>
      )}
    </div>
  );
}

function IndexStatusCell({ job }: { job: ProjectJob }) {
  const summary = job.indexStatusSummary;
  if (!summary) {
    return (
      <span className="d-flex align-items-center h-100 w-100 text-secondary">
        —
      </span>
    );
  }
  return (
    <div className="history-index-status d-flex flex-wrap align-items-center gap-1 h-100 w-100">
      <SubtleBadge
        bg="success"
        pill
        className="fs--2"
        style={{ lineHeight: 1.35 }}
      >
        {formatFaNumber(summary.indexed)} ایندکس‌شده
      </SubtleBadge>
      <SubtleBadge
        bg="warning"
        pill
        className="fs--2"
        style={{ lineHeight: 1.35 }}
      >
        {formatFaNumber(summary.notIndexed)} ایندکس‌نیست
      </SubtleBadge>
    </div>
  );
}

function jobMetaError(job: ProjectJob): {
  httpStatus?: number;
  message?: string;
} | null {
  const metaError = job.meta?.error;
  if (!metaError || typeof metaError !== 'object') return null;
  const err = metaError as {
    httpStatus?: unknown;
    message?: unknown;
  };
  const httpStatus =
    typeof err.httpStatus === 'number' ? err.httpStatus : undefined;
  const message = typeof err.message === 'string' ? err.message.trim() : '';
  if (httpStatus == null && !message) return null;
  return {
    httpStatus,
    message: message || undefined,
  };
}

function jobRunTypeLabel(job: ProjectJob): string {
  if ((job.trigger ?? 'manual') === 'schedule') {
    return job.scheduleName?.trim() || 'زمان‌بندی';
  }
  return 'دستی';
}

export function RequestHistory({
  title,
  jobs,
  trigger,
  selectedId,
  onSelect,
  onRetry,
  retryingJobId = null,
  onStop,
  stoppingJobId = null,
  loading = false,
  inspectBulkActions,
  indexBulkActions,
}: {
  title: string;
  jobs: ProjectJob[];
  /** `all` = دستی + زمان‌بندی در یک جدول (ستون نوع اجرا) */
  trigger: ProjectJobTrigger | 'all';
  selectedId: string | null;
  onSelect: (jobId: string) => void;
  onRetry?: (job: ProjectJob) => void;
  retryingJobId?: string | null;
  onStop?: (job: ProjectJob) => void;
  stoppingJobId?: string | null;
  loading?: boolean;
  inspectBulkActions?: {
    projectId: string;
    onNotify?: BulkNotify;
  };
  indexBulkActions?: {
    projectId: string;
    onNotify?: BulkNotify;
  };
}) {
  const filtered = useMemo(
    () =>
      trigger === 'all'
        ? jobs
        : jobs.filter((job) => (job.trigger ?? 'manual') === trigger),
    [jobs, trigger],
  );
  const showType = filtered.some((job) => jobHistoryTypeLabel(job));
  const showEngine = filtered.some(
    (job) => job.kind === 'google_index' || job.kind === 'indexnow',
  );
  const showIndexStatus = filtered.some((job) => job.kind === 'inspect');
  const showRetry = !!onRetry;
  const showStop = !!onStop;

  const columns = useMemo(() => {
    const cols: Record<string, unknown>[] = [];

    if (showIndexStatus) {
      cols.push({
        field: 'indexStatusSummary',
        title: 'وضعیت ایندکس',
        type: 'string',
        flex: 1.2,
        minWidth: 150,
        tooltip: guideTooltipHtml(HISTORY_COLUMN_GUIDES.indexStatus),
        cellRenderer: (params: ICellRendererParams<ProjectJob>) =>
          params.data ? <IndexStatusCell job={params.data} /> : null,
      });
    }

    if (trigger === 'all') {
      cols.push({
        field: 'trigger',
        title: 'نوع اجرا',
        type: 'string',
        flex: showIndexStatus ? 0.75 : 0.6,
        minWidth: showIndexStatus ? 110 : 90,
        tooltip: guideTooltipHtml(HISTORY_COLUMN_GUIDES.runType),
        cellRenderer: (params: ICellRendererParams<ProjectJob>) =>
          params.data ? (
            <span className="fw-semibold">{jobRunTypeLabel(params.data)}</span>
          ) : null,
      });
    } else if (trigger === 'schedule') {
      cols.push({
        field: 'scheduleName',
        title: 'زمان‌بندی',
        type: 'string',
        flex: 0.8,
        minWidth: 100,
        tooltip: guideTooltipHtml(HISTORY_COLUMN_GUIDES.schedule),
        cellRenderer: (params: ICellRendererParams<ProjectJob>) =>
          params.data?.scheduleName ? (
            <span className="fw-semibold">{params.data.scheduleName}</span>
          ) : (
            <span className="text-secondary">—</span>
          ),
      });
    }

    if (showType) {
      cols.push({
        field: 'type',
        title: 'نوع',
        type: 'string',
        flex: 0.7,
        minWidth: 128,
        maxWidth: 200,
        tooltip: guideTooltipHtml(HISTORY_COLUMN_GUIDES.type),
        cellRenderer: (params: ICellRendererParams<ProjectJob>) => {
          const typeLabel = params.data
            ? jobHistoryTypeLabel(params.data)
            : null;
          return typeLabel ? (
            <span>{typeLabel}</span>
          ) : (
            <span className="text-secondary">—</span>
          );
        },
      });
    }

    if (showEngine) {
      cols.push({
        field: 'kind',
        title: 'موتور جستجو',
        type: 'string',
        flex: 0.75,
        minWidth: 130,
        maxWidth: 180,
        tooltip: guideTooltipHtml(HISTORY_COLUMN_GUIDES.engine),
        cellRenderer: (params: ICellRendererParams<ProjectJob>) => {
          const engine = params.data
            ? jobSearchEngineLabel(params.data.kind)
            : undefined;
          if (!engine) {
            return <span className="text-secondary">—</span>;
          }
          return (
            <span className="d-inline-flex align-items-center gap-1">
              <IndexerIcon name={engine.icon} className="flex-shrink-0" />
              {engine.text}
            </span>
          );
        },
      });
    }

    cols.push({
      field: 'state',
      title: 'نتیجه',
      type: 'string',
      flex: 2.2,
      minWidth: 260,
      autoHeight: true,
      wrapText: true,
      tooltip: guideTooltipHtml(HISTORY_COLUMN_GUIDES.outcome),
      cellRenderer: (params: ICellRendererParams<ProjectJob>) => {
        const job = params.data;
        if (!job) return null;
        return (
          <OutcomeCell
            job={job}
            showRetry={showRetry}
            retryBusy={!!retryingJobId}
            retryingThis={retryingJobId === job.id}
            onRetry={onRetry}
            showStop={showStop}
            stopBusy={!!stoppingJobId}
            stoppingThis={stoppingJobId === job.id}
            onStop={onStop}
          />
        );
      },
    });

    cols.push({
      field: 'createdAt',
      title: 'زمان درخواست',
      type: 'string',
      flex: 1,
      minWidth: 130,
      tooltip: guideTooltipHtml(HISTORY_COLUMN_GUIDES.requestedAt),
      cellRenderer: (params: ICellRendererParams<ProjectJob>) => {
        const job = params.data;
        if (!job) return null;
        const createdIso = toUtcIso(job.createdAt) ?? undefined;
        return (
          <time
            className="history-time-cell"
            dateTime={createdIso}
            title={createdIso ? `${createdIso} (UTC)` : undefined}
          >
            <span className="history-time-abs">{formatTime(job.createdAt)}</span>
            <span className="history-time-relative text-secondary fs--2">
              ({formatRelativeFa(job.createdAt)})
            </span>
          </time>
        );
      },
    });

    cols.push({
      field: '_details',
      title: 'جزئیات',
      width: 200,
      minWidth: 180,
      maxWidth: 210,
      flex: 0,
      sortable: false,
      suppressMovable: true,
      suppressHeaderMenuButton: true,
      tooltip: guideTooltipHtml(HISTORY_COLUMN_GUIDES.details),
      cellClass: 'd-flex align-items-center flex-nowrap',
      cellRenderer: (params: ICellRendererParams<ProjectJob>) => {
        const job = params.data;
        if (!job) return null;
        return (
          <div className="d-inline-flex align-items-center">
            {showIndexStatus &&
              inspectBulkActions &&
              job.kind === 'inspect' && (
                <InspectJobBulkMenu
                  job={job}
                  projectId={inspectBulkActions.projectId}
                  onNotify={inspectBulkActions.onNotify}
                />
              )}
            {indexBulkActions && isIndexJobKind(job.kind) && (
              <IndexJobBulkMenu
                job={job}
                projectId={indexBulkActions.projectId}
                onNotify={indexBulkActions.onNotify}
              />
            )}
            <Button
              size="sm"
              variant="outline-secondary"
              className="history-details-btn d-inline-flex align-items-center fs--2"
              aria-label="جزئیات"
              onClick={(event) => {
                event.stopPropagation();
                onSelect(job.id);
              }}
            >
              <span>جزئیات</span>
              <IndexerIcon name="chevron-left" className="ms-1" />
            </Button>
          </div>
        );
      },
    });

    return cols;
  }, [
    showIndexStatus,
    showType,
    showEngine,
    showRetry,
    showStop,
    trigger,
    retryingJobId,
    stoppingJobId,
    onRetry,
    onStop,
    onSelect,
    inspectBulkActions,
    indexBulkActions,
  ]);

  const otherOptions = useMemo(
    () => ({
      getRowId: (params: { data: ProjectJob }) => params.data.id,
      animateRows: false,
      suppressHorizontalScroll: true,
      getRowStyle: (params: { data?: ProjectJob }) =>
        params.data && selectedId === params.data.id
          ? {
              background:
                'var(--falcon-tertiary-bg, var(--bs-tertiary-bg))',
            }
          : undefined,
      onRowClicked: (event: RowClickedEvent<ProjectJob>) => {
        if (!event.data) return;
        const target = event.event?.target as HTMLElement | null;
        if (target?.closest('button, a, .dropdown-menu, .dropdown')) return;
        onSelect(event.data.id);
      },
    }),
    [onSelect, selectedId],
  );

  return (
    <Card>
      <FalconCardHeader
        light
        titleTag="h6"
        titleClass="mb-0"
        className="py-2"
        title={
          <span>
            {title}
            <span className="text-secondary fw-normal small">
              {' · '}
              {HISTORY_WINDOW_LABEL}
            </span>
          </span>
        }
        endEl={
          filtered.length > 0 ? (
            <span className="text-secondary fw-normal small">
              {formatFaNumber(filtered.length)} درخواست
            </span>
          ) : undefined
        }
      />
      <Card.Body className="p-2">
        {loading && filtered.length === 0 ? (
          <IndexerTablePreload rows={6} cols={4} />
        ) : filtered.length === 0 ? (
          <div className="d-flex flex-column align-items-center justify-content-center gap-2 text-center text-secondary py-5">
            <IndexerIcon name="clock-history" />
            <p className="mb-0">
              {trigger === 'all'
                ? `در ${HISTORY_WINDOW_LABEL} هنوز درخواستی ثبت نشده است.`
                : trigger === 'manual'
                  ? `در ${HISTORY_WINDOW_LABEL} هنوز درخواست دستی ثبت نشده است.`
                  : `در ${HISTORY_WINDOW_LABEL} هنوز اجرای زمان‌بندی‌شده‌ای ثبت نشده است.`}
            </p>
          </div>
        ) : (
          <IndexerAgGrid
            tableName="indexer_request_history"
            columns={columns}
            data={filtered}
            otherOptions={otherOptions}
          />
        )}
      </Card.Body>
    </Card>
  );
}
